"use client";
import { useEffect, useRef, useState } from "react";
import { MdSearch, MdClose } from "react-icons/md";

export default function SearchBar({
    name = "q",
    placeholder = "Search…",
    value, // controlled mode
    defaultValue = "",
    onChange, // immediate input change
    onDebouncedChange, // debounced version
    debounceMs = 300,
    onClear,
    action = "",
    className = "",
    allowSubmit = false,
    ...props
}) {
    const inputRef = useRef(null);
    const isControlled = value !== undefined;

    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const [debouncedValue, setDebouncedValue] = useState(
        isControlled ? value : defaultValue
    );

    const effectiveValue = isControlled ? value : uncontrolledValue;

    // Debounce effect
    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedValue(effectiveValue);
            if (onDebouncedChange) onDebouncedChange(effectiveValue);
        }, debounceMs);
        return () => clearTimeout(timeout);
    }, [effectiveValue, onDebouncedChange, debounceMs]);

    // Keep ref in sync
    useEffect(() => {
        if (!isControlled && inputRef.current) {
            inputRef.current.value = uncontrolledValue;
        }
    }, [uncontrolledValue, isControlled]);

    const handleClear = () => {
        if (onClear) onClear();
        if (isControlled) {
            onChange?.({ target: { value: "" } });
        } else {
            setUncontrolledValue("");
            if (inputRef.current) {
                inputRef.current.value = "";
                inputRef.current.focus();
            }
        }
    };

    const handleInputChange = (e) => {
        const newVal = e.target.value;
        if (isControlled) {
            onChange?.(e);
        } else {
            setUncontrolledValue(newVal);
        }
    };

    return (
        <form
            method="get"
            action={action}
            className={`relative flex items-center w-full max-w-md ${className}`}
            autoComplete="off"
            onSubmit={(e) => {
                if (!allowSubmit || onChange) e.preventDefault();
            }}
            {...props}
        >
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <MdSearch className="w-5 h-5" />
            </span>
            <input
                type="search"
                name={name}
                ref={inputRef}
                placeholder={placeholder}
                aria-label={placeholder}
                value={isControlled ? value : undefined}
                defaultValue={isControlled ? undefined : defaultValue}
                onChange={handleInputChange}
                className="no-native-clear w-full py-2 pl-11 pr-10 rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary transition text-base"
            />
            {!!effectiveValue && (
                <button
                    type="button"
                    onClick={handleClear}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary"
                    aria-label="Clear search"
                >
                    <MdClose className="w-5 h-5" />
                </button>
            )}
        </form>
    );
}
